import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import Sidebar from '../components/Sidebar';
import { toast } from 'sonner';
import {
  Ticket, PlusCircle, Users, Building2, BarChart3, Clock,
  AlertTriangle, CheckCircle2, ArrowUpRight, TrendingUp, RefreshCw,
  Bell
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { DispatchView, NotificationPrefsView, SlaMonitorView, DepartmentsView, LeaderboardView, ReportsView, BillingView } from '../components/dashboard';
import WidgetDashboard from '../components/dashboard/WidgetDashboard';
import AnalyticsView from '../components/dashboard/AnalyticsView';
import { useRealtimeAlerts } from '../hooks/useRealtimeAlerts';

const STATUS_COLORS = {
  new: 'badge-new', assigned: 'badge-assigned', in_progress: 'badge-in_progress',
  pending_approval: 'badge-pending_approval', resolved: 'badge-resolved',
  closed: 'badge-closed', escalated: 'badge-escalated',
};
const PRIORITY_COLORS = { low: 'badge-low', medium: 'badge-medium', high: 'badge-high', critical: 'badge-critical' };

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const role = user?.role || 'customer';

  const defaultTab = {
    customer: 'Dashboard', technician: 'Dashboard',
    business_admin: 'Dashboard', platform_admin: 'Dashboard',
  }[role];

  const [activeTab, setActiveTab] = useState(defaultTab);
  const [tickets, setTickets] = useState([]);
  const [severityFilter, setSeverityFilter] = useState('critical');

  // Real-time WebSocket alerts for SLA breaches, critical tickets
  useRealtimeAlerts(!!user);

  // Sync tab from URL query param (for cross-page navigation)
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam) setActiveTab(tabParam);
  }, [searchParams]);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [techProfile, setTechProfile] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);

  // Poll payment status if returning from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const plan = searchParams.get('plan');
    if (sessionId) {
      const pollStatus = async (attempts = 0) => {
        if (attempts >= 5) return;
        try {
          const { data } = await api.get(`/api/payments/status/${sessionId}`);
          if (data.payment_status === 'paid') {
            toast.success(`Successfully subscribed to ${plan || 'plan'}!`);
            return;
          }
        } catch (e) { /* handled */ }
        setTimeout(() => pollStatus(attempts + 1), 2000);
      };
      pollStatus();
    }
  }, [searchParams]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [ticketsRes] = await Promise.all([
        api.get('/api/tickets?limit=50'),
      ]);
      setTickets(ticketsRes.data.tickets || []);

      if (role === 'platform_admin') {
        const [statsRes, usersRes, orgsRes, techsRes] = await Promise.all([
          api.get('/api/admin/stats'),
          api.get('/api/admin/users?limit=50'),
          api.get('/api/organizations'),
          api.get('/api/technicians'),
        ]);
        setStats(statsRes.data);
        setUsers(usersRes.data.users || []);
        setOrganizations(orgsRes.data.organizations || []);
        setTechnicians(techsRes.data.technicians || []);
      }

      if (role === 'technician') {
        try {
          const { data } = await api.get('/api/technicians/profile');
          setTechProfile(data);
        } catch (e) { /* handled */ }
      }

      if (role === 'business_admin') {
        try {
          const [orgsRes, techsRes] = await Promise.all([
            api.get('/api/organizations'),
            api.get('/api/technicians'),
          ]);
          setOrganizations(orgsRes.data.organizations || []);
          setTechnicians(techsRes.data.technicians || []);
        } catch (e) { /* handled */ }
      }
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  }, [role]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAssign = async (ticketId, techId) => {
    try {
      await api.put(`/api/tickets/${ticketId}`, { assigned_to: techId });
      toast.success('Technician assigned');
      fetchData();
    } catch { toast.error('Assignment failed'); }
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      await api.put(`/api/tickets/${ticketId}`, { status: newStatus });
      toast.success('Status updated');
      fetchData();
    } catch { toast.error('Update failed'); }
  };

  const handleSubscribe = async (plan) => {
    try {
      const { data } = await api.post('/api/payments/subscribe', {
        plan, origin_url: window.location.origin,
      });
      if (data.url) window.location.href = data.url;
    } catch (err) {
      toast.error('Payment setup failed');
    }
  };

  // ── Render Helpers ──────────────────────────────────────────
  const TicketTable = ({ items, showAssign = false }) => (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="ticket-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">Ticket</th>
              <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">Status</th>
              <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">Priority</th>
              <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">Type</th>
              <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">Assigned</th>
              <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">Created</th>
              {showAssign && <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {items.map((t) => (
              <tr key={t.id} className="hover:bg-gray-50 cursor-pointer transition-colors" onClick={() => navigate(`/tickets/${t.id}`)} data-testid={`ticket-row-${t.id}`}>
                <td className="px-4 py-3">
                  <p className="font-medium text-gray-900 truncate max-w-[200px]">{t.title}</p>
                  <p className="text-xs text-gray-400 font-mono">{t.id.slice(0, 8)}</p>
                </td>
                <td className="px-4 py-3"><span className={`inline-block px-2 py-0.5 text-xs font-semibold rounded ${STATUS_COLORS[t.status] || ''}`}>{t.status.replace('_', ' ')}</span></td>
                <td className="px-4 py-3"><span className={`inline-block px-2 py-0.5 text-xs font-semibold rounded ${PRIORITY_COLORS[t.priority] || ''}`}>{t.priority}</span></td>
                <td className="px-4 py-3 text-gray-600">{t.service_type}</td>
                <td className="px-4 py-3 text-gray-600">{t.assignee_name || '—'}</td>
                <td className="px-4 py-3 text-gray-400 text-xs">{new Date(t.created_at).toLocaleDateString()}</td>
                {showAssign && (
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    {!t.assigned_to && technicians.length > 0 && (
                      <Select onValueChange={(v) => handleAssign(t.id, v)}>
                        <SelectTrigger className="h-8 w-32 text-xs"><SelectValue placeholder="Assign" /></SelectTrigger>
                        <SelectContent>
                          {technicians.map((tech) => (
                            <SelectItem key={tech.user_id} value={tech.user_id}>{tech.user_name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {items.length === 0 && <p className="text-center text-gray-400 text-sm py-8">No tickets found</p>}
    </div>
  );

  const StatCard = ({ icon: Icon, label, value, color = 'text-gray-900' }) => (
    <div className="bg-white border border-gray-200 rounded-lg p-5 card-hover">
      <div className="flex items-center justify-between mb-3">
        <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center">
          <Icon className="w-4 h-4 text-gray-600" strokeWidth={1.5} />
        </div>
      </div>
      <p className={`font-heading text-2xl font-bold tracking-tight ${color}`}>{value}</p>
      <p className="text-xs text-gray-500 mt-1">{label}</p>
    </div>
  );

  // ── Tab Content ─────────────────────────────────────────────
  const renderContent = () => {
    if (loading) return <div className="flex items-center justify-center py-20"><RefreshCw className="w-5 h-5 animate-spin text-gray-400" /></div>;

    // Dashboard overview (all roles)
    if (activeTab === 'Dashboard') {
      const openCount = tickets.filter(t => ['new', 'assigned', 'in_progress'].includes(t.status)).length;
      const resolvedCount = tickets.filter(t => ['resolved', 'closed'].includes(t.status)).length;
      const criticalCount = tickets.filter(t => t.priority === 'critical').length;
      const highCount = tickets.filter(t => t.priority === 'high').length;
      const mediumCount = tickets.filter(t => t.priority === 'medium').length;
      const lowCount = tickets.filter(t => t.priority === 'low').length;

      const severityLevels = [
        { k: 'critical', l: 'Critical', count: criticalCount, color: 'bg-red-600 text-white', ring: 'ring-red-300' },
        { k: 'high', l: 'High', count: highCount, color: 'bg-orange-500 text-white', ring: 'ring-orange-300' },
        { k: 'medium', l: 'Medium', count: mediumCount, color: 'bg-amber-500 text-white', ring: 'ring-amber-300' },
        { k: 'low', l: 'Low', count: lowCount, color: 'bg-gray-400 text-white', ring: 'ring-gray-300' },
        { k: 'all', l: 'All', count: openCount, color: 'bg-[#D4A843] text-[#0A0A0A] font-semibold', ring: 'ring-blue-300' },
      ];
      const activeSev = severityFilter || 'critical';
      const filteredTickets = activeSev === 'all' ? tickets : tickets.filter(t => t.priority === activeSev);

      return (
        <div className="space-y-6 animate-fade-in" data-testid="dashboard-overview">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900">
                {role === 'platform_admin' ? 'Platform Overview' : role === 'technician' ? 'My Queue' : 'Dashboard'}
              </h1>
              <p className="text-sm text-gray-500 mt-1">Welcome back, {user?.name}</p>
            </div>
            {(role === 'customer' || role === 'business_admin') && (
              <Button onClick={() => navigate('/tickets/new')} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg" data-testid="create-ticket-btn">
                <PlusCircle className="w-4 h-4 mr-2" /> New Ticket
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={Ticket} label="Open Tickets" value={stats?.open_tickets ?? openCount} />
            <StatCard icon={CheckCircle2} label="Resolved" value={stats?.resolved_tickets ?? resolvedCount} color="text-emerald-600" />
            <StatCard icon={AlertTriangle} label="Critical" value={criticalCount} color="text-red-600" />
            <StatCard icon={TrendingUp} label={role === 'platform_admin' ? 'Total Users' : 'Total Tickets'} value={role === 'platform_admin' ? stats?.total_users ?? 0 : tickets.length} />
          </div>

          {/* Severity Toggle */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-lg font-bold text-gray-900">Tickets by Severity</h2>
              <div className="flex gap-1" data-testid="severity-toggle">
                {severityLevels.map(s => (
                  <button key={s.k} onClick={() => setSeverityFilter(s.k)} className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${activeSev === s.k ? `${s.color} ring-2 ${s.ring} shadow-sm` : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-testid={`severity-${s.k}`}>
                    {s.l} ({s.count})
                  </button>
                ))}
              </div>
            </div>
            <TicketTable items={filteredTickets.slice(0, 15)} showAssign={role === 'platform_admin' || role === 'business_admin'} />
            {filteredTickets.length === 0 && <p className="text-center py-6 text-sm text-gray-400 bg-white rounded-lg border border-gray-200">No {activeSev !== 'all' ? activeSev : ''} tickets found</p>}
          </div>

          <WidgetDashboard />
        </div>
      );
    }

    // Ticket lists
    if (['My Tickets', 'All Tickets', 'Tickets', 'Job Queue'].includes(activeTab)) {
      return (
        <div className="space-y-6 animate-fade-in" data-testid="tickets-view">
          <div className="flex items-center justify-between">
            <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900">{activeTab}</h1>
            {role !== 'technician' && (
              <Button onClick={() => navigate('/tickets/new')} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg" data-testid="create-ticket-btn-2">
                <PlusCircle className="w-4 h-4 mr-2" /> New Ticket
              </Button>
            )}
          </div>
          <TicketTable items={tickets} showAssign={role === 'platform_admin' || role === 'business_admin'} />
        </div>
      );
    }

    // Create Ticket shortcut
    if (activeTab === 'Create Ticket') {
      navigate('/tickets/new');
      return null;
    }

    // Service Catalog - redirect to catalog page
    if (activeTab === 'Service Catalog') {
      navigate('/services');
      return null;
    }

    // Growth Hub - redirect to growth dashboard
    if (activeTab === 'Growth Hub') {
      navigate('/growth');
      return null;
    }

    // White Label - redirect
    if (activeTab === 'White Label') {
      navigate('/whitelabel');
      return null;
    }

    // Cyber Ops - redirect
    if (activeTab === 'Cyber Ops') {
      navigate('/cyber');
      return null;
    }

    // Knowledge Base - redirect
    if (activeTab === 'Knowledge Base') {
      navigate('/kb');
      return null;
    }

    // Departments
    if (activeTab === 'Departments') {
      return <DepartmentsView />;
    }

    // Technician Profile
    if (activeTab === 'My Profile') {
      return <TechProfileView profile={techProfile} onUpdate={fetchData} />;
    }

    // Users (admin)
    if (activeTab === 'Users') {
      return <UsersView users={users} onRefresh={fetchData} />;
    }

    // Organizations
    if (activeTab === 'Organizations' || activeTab === 'Organization') {
      return <OrgsView organizations={organizations} user={user} onRefresh={fetchData} onSubscribe={handleSubscribe} />;
    }

    // Team
    if (activeTab === 'Team') {
      return (
        <div className="space-y-6 animate-fade-in" data-testid="team-view">
          <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900">Team</h1>
          <div className="bg-white border border-gray-200 rounded-lg">
            <table className="w-full text-sm">
              <thead><tr className="border-b bg-gray-50">
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Skills</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Jobs</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-100">
                {technicians.map(t => (
                  <tr key={t.id}><td className="px-4 py-3 font-medium">{t.user_name}</td>
                  <td className="px-4 py-3">{(t.skills || []).join(', ') || '—'}</td>
                  <td className="px-4 py-3"><span className={`text-xs font-semibold px-2 py-0.5 rounded ${t.availability === 'available' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>{t.availability}</span></td>
                  <td className="px-4 py-3">{t.total_jobs}</td></tr>
                ))}
              </tbody>
            </table>
            {technicians.length === 0 && <p className="text-center text-gray-400 text-sm py-8">No technicians found</p>}
          </div>
        </div>
      );
    }

    // Analytics (admin)
    if (activeTab === 'Analytics') {
      return <AnalyticsView />;
    }

    // SLA Monitor
    if (activeTab === 'SLA Monitor') {
      return <SlaMonitorView />;
    }

    // Leaderboard
    if (activeTab === 'Leaderboard') {
      return <LeaderboardView />;
    }

    // Reports
    if (activeTab === 'Reports') {
      return <ReportsView />;
    }

    // Billing
    if (activeTab === 'Billing') {
      return <BillingView />;
    }

    // Dispatch tab (admin)
    if (activeTab === 'Dispatch') {
      return <DispatchView tickets={tickets} technicians={technicians} />;
    }

    // Notifications preferences
    if (activeTab === 'Notifications') {
      return <NotificationPrefsView />;
    }

    return <p className="text-gray-400">Tab content not found</p>;
  };

  return (
    <div className="flex min-h-screen bg-gray-50" data-testid="dashboard-page">
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />
      <main className="flex-1 p-6 md:p-8 lg:p-10 overflow-y-auto">
        {renderContent()}
      </main>
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────
function TechProfileView({ profile, onUpdate }) {
  const [form, setForm] = useState({
    skills: profile?.skills?.join(', ') || '',
    certifications: profile?.certifications?.join(', ') || '',
    service_types: profile?.service_types || ['remote'],
    hourly_rate: profile?.hourly_rate || 0,
    availability: profile?.availability || 'offline',
    bio: profile?.bio || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/api/technicians/profile', {
        skills: form.skills.split(',').map(s => s.trim()).filter(Boolean),
        certifications: form.certifications.split(',').map(s => s.trim()).filter(Boolean),
        service_types: form.service_types,
        hourly_rate: parseFloat(form.hourly_rate) || 0,
        availability: form.availability,
        bio: form.bio,
      });
      toast.success('Profile updated');
      onUpdate();
    } catch { toast.error('Update failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-2xl" data-testid="tech-profile-view">
      <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900">My Profile</h1>
      <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-5">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">Availability</span>
          <div className="flex items-center gap-2">
            <Switch checked={form.availability === 'available'} onCheckedChange={(v) => setForm({...form, availability: v ? 'available' : 'offline'})} data-testid="availability-toggle" />
            <span className={`text-sm font-semibold ${form.availability === 'available' ? 'text-emerald-600' : 'text-gray-400'}`}>
              {form.availability === 'available' ? 'Available' : 'Offline'}
            </span>
          </div>
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700">Skills (comma-separated)</label>
          <Input value={form.skills} onChange={(e) => setForm({...form, skills: e.target.value})} className="mt-1 rounded-lg" placeholder="Networking, Security, Cloud" data-testid="profile-skills-input" />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700">Certifications (comma-separated)</label>
          <Input value={form.certifications} onChange={(e) => setForm({...form, certifications: e.target.value})} className="mt-1 rounded-lg" placeholder="CompTIA A+, CISSP" data-testid="profile-certs-input" />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700">Hourly Rate ($)</label>
          <Input type="number" value={form.hourly_rate} onChange={(e) => setForm({...form, hourly_rate: e.target.value})} className="mt-1 rounded-lg w-32" data-testid="profile-rate-input" />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700">Bio</label>
          <Textarea value={form.bio} onChange={(e) => setForm({...form, bio: e.target.value})} className="mt-1 rounded-lg" rows={3} placeholder="Tell customers about your expertise..." data-testid="profile-bio-input" />
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg" data-testid="profile-save-btn">
          {saving ? 'Saving...' : 'Save Profile'}
        </Button>
        {profile && (
          <div className="pt-4 border-t border-gray-200 grid grid-cols-2 gap-4">
            <div><p className="text-xs text-gray-500">Rating</p><p className="text-lg font-bold">{profile.rating_avg?.toFixed(1) || '—'}</p></div>
            <div><p className="text-xs text-gray-500">Total Jobs</p><p className="text-lg font-bold">{profile.total_jobs}</p></div>
          </div>
        )}
      </div>
    </div>
  );
}

function UsersView({ users, onRefresh }) {
  const handleRoleChange = async (userId, newRole) => {
    try {
      await api.put(`/api/admin/users/${userId}/role`, { role: newRole });
      toast.success('Role updated');
      onRefresh();
    } catch { toast.error('Update failed'); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="users-view">
      <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900">Users</h1>
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="border-b bg-gray-50">
            <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">User</th>
            <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Role</th>
            <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Provider</th>
            <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Joined</th>
            <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
          </tr></thead>
          <tbody className="divide-y divide-gray-100">
            {users.map(u => (
              <tr key={u.id} data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3">
                  <p className="font-medium text-gray-900">{u.name}</p>
                  <p className="text-xs text-gray-400">{u.email}</p>
                </td>
                <td className="px-4 py-3">
                  <Select defaultValue={u.role} onValueChange={(v) => handleRoleChange(u.id, v)}>
                    <SelectTrigger className="h-8 w-36 text-xs" data-testid={`role-select-${u.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="customer">Customer</SelectItem>
                      <SelectItem value="technician">Technician</SelectItem>
                      <SelectItem value="business_admin">Business Admin</SelectItem>
                      <SelectItem value="platform_admin">Platform Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3 text-gray-600">{u.auth_provider || 'email'}</td>
                <td className="px-4 py-3 text-gray-400 text-xs">{new Date(u.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3"><span className="font-mono text-xs text-gray-400">{u.id.slice(0, 8)}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function OrgsView({ organizations, user, onRefresh, onSubscribe }) {
  const [showCreate, setShowCreate] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    if (!orgName.trim()) return;
    setCreating(true);
    try {
      await api.post('/api/organizations', { name: orgName });
      toast.success('Organization created');
      setOrgName('');
      setShowCreate(false);
      onRefresh();
    } catch (err) { toast.error('Failed to create organization'); }
    finally { setCreating(false); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="orgs-view">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900">Organizations</h1>
        {(user?.role === 'business_admin' || user?.role === 'platform_admin') && (
          <Button onClick={() => setShowCreate(!showCreate)} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg" data-testid="create-org-btn">
            <PlusCircle className="w-4 h-4 mr-2" /> New Organization
          </Button>
        )}
      </div>

      {showCreate && (
        <div className="bg-white border border-gray-200 rounded-lg p-5 flex gap-3 items-end" data-testid="create-org-form">
          <div className="flex-1">
            <label className="text-sm font-medium text-gray-700">Organization Name</label>
            <Input value={orgName} onChange={(e) => setOrgName(e.target.value)} className="mt-1 rounded-lg" placeholder="Acme Corp" data-testid="org-name-input" />
          </div>
          <Button onClick={handleCreate} disabled={creating} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg" data-testid="org-submit-btn">
            {creating ? 'Creating...' : 'Create'}
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {organizations.map(org => (
          <div key={org.id} className="bg-white border border-gray-200 rounded-lg p-5 card-hover" data-testid={`org-card-${org.id}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-[#D4A843]/10 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-[#D4A843]" strokeWidth={1.5} />
              </div>
              <div>
                <p className="font-medium text-gray-900">{org.name}</p>
                <p className="text-xs text-gray-400">{org.member_count || 0} members</p>
              </div>
            </div>
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#D4A843] bg-[#D4A843]/10 px-2 py-0.5 rounded">{org.plan}</span>
              <span className="text-xs text-gray-400">{org.routing_mode} routing</span>
            </div>
            <div className="mt-3 flex gap-2">
              {['starter', 'growth', 'pro'].filter(p => p !== org.plan).map(plan => (
                <Button key={plan} variant="outline" size="sm" className="text-xs rounded" onClick={() => onSubscribe(plan)} data-testid={`upgrade-${plan}-btn`}>
                  Upgrade to {plan}
                </Button>
              ))}
            </div>
          </div>
        ))}
      </div>
      {organizations.length === 0 && <p className="text-center text-gray-400 text-sm py-8">No organizations found</p>}
    </div>
  );
}


// SlaMonitorView and DepartmentsView are now in /components/dashboard/
